import { useState, useMemo } from 'react';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ExpandableMultiFilterProps<T = any> {
  title: string;
  icon?: React.ReactNode;
  options: string[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  initialLimit?: number;
  className?: string;
  renderOption?: (option: string) => React.ReactNode;
  // Data filtering props
  data?: T[];
  getItemValue?: (item: T, option: string) => boolean;
  showCounts?: boolean;
  hideEmptyOptions?: boolean;
  facetCounts?: Record<string, number>;
  /** Render a search box under the title that narrows the option list (client-side). */
  searchable?: boolean;
}

// Case- and accent-insensitive key for matching ("Sao" matches "São Paulo").
const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export function ExpandableMultiFilter<T>({
  title,
  icon,
  options,
  selectedValues,
  onSelectionChange,
  initialLimit = 6,
  className = '',
  renderOption,
  data = [],
  getItemValue,
  showCounts = true,
  hideEmptyOptions = true,
  facetCounts,
  searchable = false,
}: ExpandableMultiFilterProps<T>) {
  const { t } = useTranslation('collab-club');
  const [showMore, setShowMore] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const normalizedQuery = fold(query);

  // Calculate counts for each option
  const optionCounts = useMemo(() => {
    // Use facetCounts if provided
    if (facetCounts) {
      const counts = new Map<string, number>();
      Object.entries(facetCounts).forEach(([key, value]) => {
        counts.set(key, value);
      });
      return counts;
    }

    if (!data.length || !getItemValue) return new Map<string, number>();

    const counts = new Map<string, number>();

    options.forEach((option) => {
      if (option === 'all') {
        return; // Skip calculating count for "all"
      }

      const count = data.filter((item) => getItemValue(item, option)).length;
      counts.set(option, count);
    });

    return counts;
  }, [data, options, getItemValue]);

  // Filter options based on counts (hide empty options if enabled)
  const filteredOptions = useMemo(() => {
    const byQuery = normalizedQuery
      ? options.filter((option) => option === 'all' || fold(option).includes(normalizedQuery))
      : options;
    if (!hideEmptyOptions || !data.length) return byQuery;

    return byQuery.filter((option) => {
      if (option === 'all') return true; // Always show "all" option
      const count = optionCounts.get(option) || 0;
      return count > 0 || selectedValues.includes(option); // Show if has results or is selected
    });
  }, [options, optionCounts, hideEmptyOptions, data.length, selectedValues, normalizedQuery]);

  // Collapsed: the first `initialLimit` options in facet order — selection never
  // reorders or promotes items (the "n selected" chip flags hidden selections).
  // Expanded or searching: every (matching) option.
  const displayedOptions =
    showMore || normalizedQuery ? filteredOptions : filteredOptions.slice(0, initialLimit);
  const hasMore = !normalizedQuery && filteredOptions.length > initialLimit;
  const noMatches = !!normalizedQuery && filteredOptions.length <= 1; // only "all" left

  const isAllSelected = selectedValues.includes('all') || selectedValues.length === 0;
  const isIndeterminate = !isAllSelected && selectedValues.length > 0;

  const handleAllChange = () => {
    // Always keep "All" selected when it's clicked
    onSelectionChange(['all']);
  };

  const handleOptionChange = (option: string, checked: boolean) => {
    if (option === 'all') {
      handleAllChange();
      return;
    }

    let newSelection: string[];

    if (checked) {
      // Add option and remove "all" if present (automatically unselect "all" when selecting specific options)
      newSelection = [...selectedValues.filter((v) => v !== 'all'), option];
    } else {
      // Remove option
      newSelection = selectedValues.filter((v) => v !== option);

      // If no specific options are selected, default to "all"
      if (newSelection.length === 0) {
        newSelection = ['all'];
      }
    }

    onSelectionChange(newSelection);
  };

  return (
    <div className={className}>
      <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
        {icon}
        {title}
        {searchable && !searchOpen && (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label={t('search_placeholder_generic')}
            className="ml-auto p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <Search className="h-4 w-4" />
          </button>
        )}
      </h3>
      {searchable && searchOpen && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onBlur={() => {
              if (!query.trim()) setSearchOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setQuery('');
                setSearchOpen(false);
              }
            }}
            placeholder={t('search_placeholder_generic')}
            className="pl-9 pr-8 h-9 text-sm box-border"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setQuery('');
              setSearchOpen(false);
            }}
            aria-label={t('clear_all')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="space-y-3">
        {/* All option */}
        <div className="flex items-center space-x-3">
          <Checkbox
            id={`${title.toLowerCase()}-all`}
            checked={isAllSelected}
            onCheckedChange={handleAllChange}
            className="rounded"
          />
          <label
            htmlFor={`${title.toLowerCase()}-all`}
            className="text-sm text-gray-700 capitalize cursor-pointer flex-1 font-medium"
          >
            {t('all_filter', { filter: title.toLowerCase() })}
          </label>
          {isIndeterminate && (
            <span className="text-xs text-blue-600 font-medium">
              {t('selected_items', { count: selectedValues.length })}
            </span>
          )}
        </div>

        {/* Divider */}
        {/* <div className="border-t border-gray-200 my-2" /> */}

        {/* Individual options */}
        {displayedOptions
          .filter((option) => option !== 'all')
          .map((option) => (
            <div key={option} className="flex items-center space-x-3">
              <Checkbox
                id={`${title.toLowerCase()}-${option}`}
                checked={!isAllSelected && selectedValues.includes(option)}
                onCheckedChange={(checked) => handleOptionChange(option, checked as boolean)}
                className="rounded"
              />
              <label
                htmlFor={`${title.toLowerCase()}-${option}`}
                className="text-sm text-gray-700 capitalize cursor-pointer flex-1"
              >
                <span className="flex items-center justify-between">
                  <span>{renderOption ? renderOption(option) : option}</span>
                  {showCounts && (facetCounts || data.length > 0) && (
                    <span className="text-xs text-gray-500 ml-2">
                      {facetCounts?.[option] || optionCounts.get(option) || 0}
                    </span>
                  )}
                </span>
              </label>
            </div>
          ))}

        {noMatches && <p className="text-sm text-gray-500">{t('nothing_found')}</p>}

        {hasMore && (
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-2"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
            />
            {showMore ? t('show_less') : t('show_more')}
          </button>
        )}
      </div>
    </div>
  );
}
